/**
 * PurchaseOrderDetail.js
 * ZENO ERP — Procurement Engine
 * Last updated: 31 May 2026
 *
 * TO-16 CHANGES:
 *   — approvePO(): draft → "approved" (NOT directly to "ordered")
 *   — markAsOrdered(): "approved" → "ordered" (new function)
 *   — canMarkOrdered() guard added
 *   — "Mark as Ordered" button shown when po_status === "approved" (SM/Owner only)
 *   — Post-approve modal updated — reflects "approved" state, not "ordered"
 *   — Notification: PO approved → store notified (non-blocking)
 *   — Notification: PO ordered → store notified (non-blocking)
 *   — STATUS_UI already had "approved" entry ✅
 *
 * EXISTING (unchanged):
 *   — canReceive(): GRN only when po_status === "ordered" ✅
 *   — canAmend(po, role): Owner / SM / Manager only (HIGH-01), blocked after ordered ✅
 *   — canApprove(): draft only ✅
 *   — Amendment flow ✅
 *   — PDF download ✅
 *   — Email to vendor ✅
 *   — grand_total = received_qty × rate ✅
 *
 * FIXES APPLIED (BUG 9 + BUG 10 — unchanged):
 *   — loadItemsMaster: dual-query approach
 *   — Dedup by normalized item name
 *   — amendment_log[] populated correctly
 *
 * D-291 P3c (20 Oct 2026):
 *   — The page shows the ORDERED value (qty × rate before GST − discount =
 *     taxable, + GST = total) from computeOrderValue(); after a GRN it also
 *     shows the received value (received qty × GRN rate), clearly labelled.
 *     The stored grand_total stays the received value (Tally / reports).
 *   — Amend, Approve and Mark as Ordered go through purchaseOrderService
 *     (one transaction each, refused if the PO changed since this page
 *     loaded it). Amend edits rate before GST, discount, GST %, HSN and the
 *     "Rate not agreed yet" tick; switching the item takes the NEW item's
 *     code / HSN / GST; lines are merged by line_id. Amending an approved PO
 *     sends it back to Draft. Approve saves approved_value.
 *   — An Unregistered vendor's PO cannot be approved while lines carry GST.
 *   — PDF + vendor email show the rate before GST, GST % and the order value.
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
// HIGH-01: role lists shared with the service (the rules mirror them).
import {
  PO_APPROVER_ROLES, PO_AMEND_ROLES, PO_REASSIGN_ROLES, SITE_FINALIZE_ROLES, PO_GRN_ROLES, hasPoRole,
} from "../../utils/poRoles";
import {
  amendPurchaseOrder, approvePurchaseOrder, markPurchaseOrderOrdered, reassignPOSiteEngineers,
} from "../services/purchase/purchaseOrderService";
import { computeOrderValue, supplyTypeOf, vendorGstinOf, vendorGstStatus, checkHsn } from "../../utils/gstBill";
import { qtyStepFor, hasUnit } from "../../utils/units";
import { checkPOLines } from "../../utils/poLines";
import { pickerItems, searchPicker, typedNameLink, itemOptionLabel, ambiguousNameMessage } from "../../utils/itemPicker";
import UnitPicker from "../../components/purchase/UnitPicker";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { useCompany } from "../../context/CompanyContext";
import { markNotificationReadByReference } from "../../services/notificationService";
import { canEnterBills } from "../../services/purchaseBillService";

// ─── Category allowlist for Amend PO item search ──────────────────────────────
const PURCHASE_ALLOWED_CATEGORIES = ["MATERIAL", "CONSUMABLE", "ASSET"];
function isPurchaseCategory(cat) {
  if (!cat) return true;
  return PURCHASE_ALLOWED_CATEGORIES.includes(cat.toUpperCase().trim());
}

// ─── EmailJS Config ───────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = "YOUR_SERVICE_ID";
const EMAILJS_TEMPLATE_ID = "YOUR_TEMPLATE_ID";
const EMAILJS_PUBLIC_KEY  = "YOUR_PUBLIC_KEY";

// ─── Status display map ───────────────────────────────────────────────────────
const STATUS_UI = {
  draft:          { label: "Draft",          color: "bg-gray-100 text-gray-700" },
  approved:       { label: "Approved",       color: "bg-blue-100 text-blue-700" },
  ordered:        { label: "Ordered",        color: "bg-indigo-100 text-indigo-700" },
  completed:      { label: "Completed",      color: "bg-green-100 text-green-700" },
  short_closed:   { label: "Short Closed",   color: "bg-yellow-100 text-yellow-800" },
  short:          { label: "Short",          color: "bg-yellow-100 text-yellow-800" },
  over:           { label: "Over Received",  color: "bg-orange-100 text-orange-800" },
  over_received:  { label: "Over Received",  color: "bg-orange-100 text-orange-800" },
  closed:         { label: "Closed",         color: "bg-gray-200 text-gray-600" },
  cancelled:      { label: "Cancelled",      color: "bg-red-100 text-red-700" },
};

// ─── Guards ───────────────────────────────────────────────────────────────────
// Role lists come from src/utils/poRoles.js (HIGH-01).

// canApprove: only draft POs, only approver roles
function canApprove(po, userRole) {
  if (!po) return false;
  if (po.po_status !== "draft") return false;
  if (!hasPoRole(PO_APPROVER_ROLES, userRole)) return false;
  return true;
}

// ── TO-16: canMarkOrdered — approved → ordered ────────────────────────────────
// Only SM/Owner can mark as ordered. Only when po_status === "approved".
function canMarkOrdered(po, userRole) {
  if (!po) return false;
  if (po.po_status !== "approved") return false;
  if (!hasPoRole(PO_APPROVER_ROLES, userRole)) return false;
  return true;
}

// canReceive: GRN only when po_status === "ordered"
const TERMINAL_PO_STATUSES = [
  "completed", "short_closed", "short", "over", "over_received", "closed", "cancelled",
];
const GRN_ALLOWED_STATUSES = ["ordered"];

function canReceive(po) {
  if (!po) return false;
  if (po.grn_locked === true) return false;
  if (TERMINAL_PO_STATUSES.includes(po.po_status)) return false;
  if (!GRN_ALLOWED_STATUSES.includes(po.po_status)) return false;
  return true;
}

// canAmend: Owner / SM / Manager only (HIGH-01), blocked after ordered
function canAmend(po, userRole) {
  if (!po) return false;
  if (!hasPoRole(PO_AMEND_ROLES, userRole)) return false;
  if (po.grn_locked === true) return false;
  if ([
    "ordered", "completed", "short_closed", "short",
    "over", "over_received", "closed", "cancelled",
  ].includes(po.po_status)) return false;
  return true;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// RECEIVED value = received_qty × rate (the GRN rate) — same as the stored
// grand_total. The ORDERED value comes from computeOrderValue() (D-291 P3c).
function calcGrandTotal(items = []) {
  return items.reduce((sum, item) => {
    if (item._cancelled) return sum;
    const qty  = parseFloat(item.received_qty) || 0;
    const rate = parseFloat(item.rate) || 0;
    return sum + qty * rate;
  }, 0);
}

// The agreed rate before GST of a PO line. Old Direct PO lines carried it
// only in `rate` (before Rate Breakdown existed).
function agreedRateOf(item = {}) {
  const b = Number(item.base_rate);
  return Number.isFinite(b) && item.base_rate !== "" && item.base_rate !== null && item.base_rate !== undefined
    ? b
    : Number(item.rate || 0);
}

// Item Master gst_percent is a number or null (not set) → the box value.
const gstBoxValue = (gst) => (gst === null || gst === undefined || gst === "" ? "" : String(gst));

// P3c-8: an amend row linked to an Item Master item - by a click in the list
// AND by a typed name that is exactly that item's name.
// D-291 P3c: switching the item takes the NEW item's code, HSN and GST % (an
// item without GST % leaves the box empty to be typed). P3c-7: the NEW item's
// unit, read-only; none in Item Master -> pick one.
const amendRowFromMaster = (r, masterItem) => ({
  ...r, item_id: masterItem.item_id || "", item_name: masterItem.item_name, item_category: masterItem.item_category || "",
  item_code: masterItem.item_code || "", hsn_code: masterItem.hsn_code || "",
  gst_percent: gstBoxValue(masterItem.gst_percent), _isCustom: false,
  unit: hasUnit(masterItem.unit) ? masterItem.unit : "",
  _unitLocked: hasUnit(masterItem.unit), _unitMasterMissing: !hasUnit(masterItem.unit),
});

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function PurchaseOrderDetail() {
  const { poId }  = useParams();
  const navigate  = useNavigate();
  const { currentUser } = useAuth();
  const { company }     = useCompany();

  // ── Core state ──────────────────────────────────────────────────────────────
  const [po,      setPo]      = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // ── Amend PO state ──────────────────────────────────────────────────────────
  const [showAmend,   setShowAmend]   = useState(false);
  const [amendItems,  setAmendItems]  = useState([]);
  const [amendReason, setAmendReason] = useState("");
  const [amendSaving, setAmendSaving] = useState(false);
  const [amendError,  setAmendError]  = useState(null);
  // Shown after an amend that sent an approved PO back to Draft.
  const [amendNotice, setAmendNotice] = useState(null);

  // ── Vendor doc (D-291 P3c) — its GST status decides "no GST" for an
  // Unregistered vendor and the CGST/SGST vs IGST split on this page.
  const [vendorDoc, setVendorDoc] = useState(null);

  // ── Approval state ──────────────────────────────────────────────────────────
  const [approving,    setApproving]    = useState(false);
  const [approveError, setApproveError] = useState(null);

  // ── Site Engineer reassignment state (Direct Site Purchase) ────────────────
  const [projectSiteEngineers, setProjectSiteEngineers] = useState([]);
  const [reassignSelection,    setReassignSelection]    = useState([]);
  const [reassigning,          setReassigning]          = useState(false);
  const [reassignError,        setReassignError]        = useState(null);

  // ── TO-16: Mark as Ordered state ────────────────────────────────────────────
  const [markingOrdered,    setMarkingOrdered]    = useState(false);
  const [markOrderedError,  setMarkOrderedError]  = useState(null);

  // ── Post-approve action modal ────────────────────────────────────────────────
  const [showPostApprove,   setShowPostApprove]   = useState(false);
  const [postApproveVendor, setPostApproveVendor] = useState(null);
  const [emailSending,      setEmailSending]      = useState(false);
  const [emailStatus,       setEmailStatus]       = useState(null);
  const [approvedPOData,    setApprovedPOData]    = useState(null);

  // ── Items master (for Amend) ─────────────────────────────────────────────────
  const [itemsMaster,   setItemsMaster]   = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const itemsMasterRef  = useRef([]);

  // ── Per-row search state ─────────────────────────────────────────────────────
  const [searchState, setSearchState] = useState({});
  const searchRefs  = useRef({});
  const reasonRef   = useRef(null);

  // ── Load PO ──────────────────────────────────────────────────────────────────
  const loadPO = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const snap = await getDoc(doc(db, "purchase_orders", poId));
      if (!snap.exists()) { setError("Purchase order not found."); return; }
      const data = { id: snap.id, ...snap.data() };
      if (data.company_id !== currentUser.company_id) { setError("Access denied."); return; }
      setPo(data);
    } catch (e) {
      console.error("[PurchaseOrderDetail] loadPO error:", e);
      setError("Failed to load purchase order.");
    } finally {
      setLoading(false);
    }
  }, [poId, currentUser.company_id]);

  // Silent reload — no loading screen, no redirect
  const silentReloadPO = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, "purchase_orders", poId));
      if (!snap.exists()) return;
      const data = { id: snap.id, ...snap.data() };
      if (data.company_id !== currentUser.company_id) return;
      setPo(data);
    } catch (e) {
      console.error("[silentReloadPO] error:", e);
    }
  }, [poId, currentUser.company_id]);

  useEffect(() => { loadPO(); }, [loadPO]);

  // D-291 P3c: the vendor's current GST status (read once per vendor).
  const poVendorId = po?.vendor_id || "";
  useEffect(() => {
    if (!poVendorId) { setVendorDoc(null); return; }
    let cancelled = false;
    getDoc(doc(db, "vendors", poVendorId))
      .then(snap => {
        if (cancelled) return;
        const v = snap.exists() ? snap.data() : null;
        setVendorDoc(v && v.company_id === currentUser.company_id ? v : null);
      })
      .catch(e => console.error("[PurchaseOrderDetail] vendor load failed (non-blocking):", e));
    return () => { cancelled = true; };
  }, [poVendorId, currentUser.company_id]);

  // D-291 P3d-5a: the receipt of this PO and the vendor's bill entered for it
  // (both company-checked). Drives the "waiting for the bill / bill entered"
  // Next step card. Read only.
  const [billInfo, setBillInfo] = useState(null);   // { receipt, bill } | null
  const poReceiptId = po?.purchase_receipt_id || "";
  useEffect(() => {
    if (!poReceiptId) { setBillInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const rSnap = await getDoc(doc(db, "purchase_receipts", poReceiptId));
        const receipt = rSnap.exists() ? { id: rSnap.id, ...rSnap.data() } : null;
        if (!receipt || receipt.company_id !== currentUser.company_id) { if (!cancelled) setBillInfo(null); return; }
        let bill = null;
        if (receipt.purchase_bill_id) {
          // MED-32: only bill-entry / Accounts roles may read a bill. For anyone else the
          // read is refused - keep the receipt (Next step card) and show no bill.
          const bSnap = await getDoc(doc(db, "purchase_bills", receipt.purchase_bill_id)).catch(() => null);
          bill = bSnap && bSnap.exists() ? { id: bSnap.id, ...bSnap.data() } : null;
          if (bill && bill.company_id !== currentUser.company_id) bill = null;
        }
        if (!cancelled) setBillInfo({ receipt, bill });
      } catch (e) {
        console.error("[PurchaseOrderDetail] bill status load failed (non-blocking):", e);
        if (!cancelled) setBillInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [poReceiptId, currentUser.company_id]);

  // Load the project's assigned Site Engineers once the PO (with its
  // project_id) is known — powers the reassignment picker below. Depends
  // on assigned_site_engineer_ids' *content* (joined string), not the
  // array reference itself — every silentReloadPO() creates a new array
  // even when the value hasn't changed, which would otherwise re-run this
  // effect (and re-sync reassignSelection) on every unrelated reload.
  const assignedIdsKey = (po?.assigned_site_engineer_ids || []).join(",");

  useEffect(() => {
    const loadEngineers = async () => {
      if (po?.purchase_mode !== "direct_site_purchase" || !po?.project_id) {
        setProjectSiteEngineers([]);
        return;
      }
      try {
        const projSnap = await getDoc(doc(db, "projects", po.project_id));
        const ids = projSnap.exists() ? (projSnap.data().site_engineer_ids || []) : [];
        if (ids.length === 0) { setProjectSiteEngineers([]); return; }
        const snap = await getDocs(query(
          collection(db, "users"),
          where("company_id", "==", currentUser.company_id || ""),
          where("role", "==", "site_engineer")
        ));
        setProjectSiteEngineers(
          snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => ids.includes(u.id))
        );
        setReassignSelection(po.assigned_site_engineer_ids || []);
      } catch (e) {
        console.error("[PurchaseOrderDetail] loadEngineers error:", e);
      }
    };
    loadEngineers();
  }, [po?.purchase_mode, po?.project_id, assignedIdsKey, currentUser.company_id]);

  const handleReassign = useCallback(async () => {
    if (!po) return;
    try {
      setReassigning(true);
      setReassignError(null);
      // HIGH-01: one transaction, refused if the PO changed since this page loaded it.
      await reassignPOSiteEngineers({
        po_id: po.id,
        company_id: currentUser.company_id,
        loaded_updated_at: po.updated_at,
        assigned_site_engineer_ids: reassignSelection,
        user: currentUser,
      });
      await silentReloadPO();
    } catch (e) {
      console.error("[handleReassign] error:", e);
      setReassignError(e.message || "Failed to update assignment.");
    } finally {
      setReassigning(false);
    }
  }, [po, currentUser, reassignSelection, silentReloadPO]);

  // ── loadItemsMaster ───────────────────────────────────────────────────────────
  const loadItemsMaster = useCallback(async () => {
    if (!currentUser?.company_id) return;
    try {
      setMasterLoading(true);
      // P4-3: own + shared "" items, every item listed (names repeat).
      const snap = await getDocs(query(collection(db, "items"), where("company_id", "in", [currentUser.company_id, ""])));
      const list = pickerItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })), currentUser.company_id, {
        allow: (item) => isPurchaseCategory(item.item_category || item.category || null),
      });

      itemsMasterRef.current = list;
      setItemsMaster(list);
    } catch (e) {
      console.error("[loadItemsMaster] Error:", e);
    } finally {
      setMasterLoading(false);
    }
  }, [currentUser?.company_id]);

  // ── sendVendorEmail ───────────────────────────────────────────────────────────
  const sendVendorEmail = useCallback(async (poData, vendorEmail, vendorContactPerson) => {
    if (!vendorEmail?.trim()) return { success: false, reason: "No vendor email on file" };
    if (
      EMAILJS_SERVICE_ID  === "YOUR_SERVICE_ID" ||
      EMAILJS_TEMPLATE_ID === "YOUR_TEMPLATE_ID" ||
      EMAILJS_PUBLIC_KEY  === "YOUR_PUBLIC_KEY"
    ) {
      console.warn("[sendVendorEmail] EmailJS not configured");
      return { success: false, reason: "EmailJS not configured" };
    }
    try {
      // D-291 P3c: rate before GST + GST % per line; total = the ordered
      // value incl. GST (computeOrderValue), never received × rate.
      const itemsText = (poData.items || [])
        .filter(i => !i._cancelled)
        .map((item, idx) => {
          const rateText = item.rate_not_agreed === true || !(agreedRateOf(item) > 0)
            ? "rate to be quoted"
            : `@ ₹${inr(agreedRateOf(item))} + GST ${item.gst_percent ?? "—"}%`;
          return `${idx + 1}. ${item.item_name} — Qty: ${item.ordered_qty || 0} ${item.unit || ""} ${rateText}`;
        })
        .join("\n");

      const grandTotal = computeOrderValue(poData.items || [], null).total;

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          to_email:      vendorEmail,
          to_name:       vendorContactPerson || poData.vendor_name || "Vendor",
          po_number:     poData.po_number || poData.id || "",
          company_name:  currentUser.company_name || "ZENO HVAC ERP",
          vendor_name:   poData.vendor_name || "",
          items_table:   itemsText,
          grand_total:   `₹${grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
          approved_by:   currentUser.name || currentUser.email || "Management",
          approved_date: new Date().toLocaleDateString("en-IN"),
          po_date:       poData.created_at?.toDate
            ? poData.created_at.toDate().toLocaleDateString("en-IN")
            : new Date().toLocaleDateString("en-IN"),
        },
        EMAILJS_PUBLIC_KEY
      );
      return { success: true };
    } catch (e) {
      console.error("[sendVendorEmail] Error:", e);
      return { success: false, reason: e.message };
    }
  }, [currentUser]);

  // ── TO-16: approvePO — draft → "approved" ────────────────────────────────────
  // CHANGE: saves po_status: "approved" (not "ordered")
  // Store is notified via notification (non-blocking)
  const approvePO = useCallback(async () => {
    if (!po) return;
    if (!canApprove(po, currentUser.role)) {
      setApproveError("You don't have permission to approve this PO.");
      return;
    }
    try {
      setApproving(true);
      setApproveError(null);

      // D-291 P3c: ONE transaction in the service — refused if the PO changed
      // since this page loaded it (so nobody approves a value they did not
      // see), if an Unregistered vendor's lines carry GST, or if a line fails
      // checkPOLines() (the same check as Amend). Saves approved_value (the
      // ordered value that was approved).
      const result = await approvePurchaseOrder({
        po_id:             poId,
        company_id:        currentUser.company_id,
        loaded_updated_at: po.updated_at,
        user:              currentUser,
      });
      const freshPO = { ...po, po_number: result.po_number || po.po_number };

      console.log("[approvePO] PO approved → approved:", poId);
      await silentReloadPO();

      // ── Resolve po_created notification ───────────────────────────────────
      try {
        await markNotificationReadByReference(poId, "po_created", currentUser.company_id);
      } catch (e) {
        console.warn("[approvePO] resolve notification failed (non-blocking):", e);
      }

      // ── TO-16: Notify store — PO approved ────────────────────────────────
      try {
        const { createNotification } = await import("../../services/notificationService");
        await createNotification({
          type:           "po_approved",
          title:          "PO Approved",
          body:           `${freshPO.po_number || "PO"} approved by ${currentUser.name || "Manager"} — ready to order`,
          route:          `/purchase-orders/${poId}`,
          for_roles:      ["sm", "store", "owner"],
          company_id:     currentUser.company_id,
          reference_id:   poId,
          reference_type: "purchase_order",
          priority:       "medium",
        });
      } catch (notifErr) {
        console.warn("[approvePO] Notification failed (non-blocking):", notifErr);
      }

      // ── Open post-approve modal ────────────────────────────────────────────
      const approvedPO = {
        ...freshPO,
        po_status:        "approved",
        approved_by_name: currentUser.name || currentUser.email,
      };
      setApprovedPOData(approvedPO);
      setEmailStatus(null);

      if (freshPO.vendor_id) {
        try {
          const vendorSnap = await getDoc(doc(db, "vendors", freshPO.vendor_id));
          if (vendorSnap.exists()) {
            const v = vendorSnap.data();
            setPostApproveVendor({
              email:          v.email || "",
              contact_person: v.contact_person || v.name || "",
              name:           freshPO.vendor_name || v.name || "",
              phone:          v.phone || v.mobile || v.contact_no || "",
            });
          } else {
            setPostApproveVendor({ email: "", contact_person: "", name: freshPO.vendor_name || "", phone: "" });
          }
        } catch {
          setPostApproveVendor({ email: "", contact_person: "", name: freshPO.vendor_name || "", phone: "" });
        }
      } else {
        setPostApproveVendor({ email: "", contact_person: "", name: freshPO.vendor_name || "", phone: "" });
      }

      setShowPostApprove(true);
    } catch (e) {
      console.error("[approvePO] Error:", e);
      // The service says why (changed by someone, GST on an unregistered
      // vendor, a line to fix) — show it as it is.
      setApproveError(e?.message || "Failed to approve PO. Please try again.");
    } finally {
      setApproving(false);
    }
  }, [po, poId, currentUser, silentReloadPO]);

  // ── TO-16: markAsOrdered — "approved" → "ordered" ────────────────────────────
  // Store generates PO PDF, sends to vendor, then marks as ordered.
  // GRN button only appears after this step.
  const markAsOrdered = useCallback(async () => {
    if (!po) return;
    if (!canMarkOrdered(po, currentUser.role)) {
      setMarkOrderedError("Only SM/Owner can mark PO as ordered.");
      return;
    }
    try {
      setMarkingOrdered(true);
      setMarkOrderedError(null);

      // D-291 P3c: ONE transaction in the service (approved → ordered only).
      const result = await markPurchaseOrderOrdered({
        po_id:      poId,
        company_id: currentUser.company_id,
        user:       currentUser,
      });
      const freshPO = { ...po, po_number: result.po_number || po.po_number };

      console.log("[markAsOrdered] PO marked as ordered:", poId);
      await silentReloadPO();

      // ── Resolve po_approved notification ──────────────────────────────────
      try {
        await markNotificationReadByReference(poId, "po_approved", currentUser.company_id);
      } catch (e) {
        console.warn("[markAsOrdered] resolve notification failed (non-blocking):", e);
      }

      // ── TO-16: Notify store — PO ordered, GRN can be done ─────────────────
      try {
        const { createNotification } = await import("../../services/notificationService");
        await createNotification({
          type:           "po_ordered",
          title:          "PO Sent to Vendor",
          body:           `${freshPO.po_number || "PO"} is now ordered — receive material when it arrives`,
          route:          `/purchase-orders/${poId}`,
          for_roles:      ["sm", "store", "owner"],
          company_id:     currentUser.company_id,
          reference_id:   poId,
          reference_type: "purchase_order",
          priority:       "medium",
        });
      } catch (notifErr) {
        console.warn("[markAsOrdered] Notification failed (non-blocking):", notifErr);
      }

    } catch (e) {
      console.error("[markAsOrdered] Error:", e);
      setMarkOrderedError(e?.message || "Failed to mark as ordered. Please try again.");
    } finally {
      setMarkingOrdered(false);
    }
  }, [po, poId, currentUser, silentReloadPO]);

  // ── downloadPO ────────────────────────────────────────────────────────────────
  const downloadPO = useCallback(() => {
    if (!po) return;

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW  = 210;
    const margin = 15;
    const contentW = pageW - margin * 2;

    const DARK     = [30,  30,  30];
    const GRAY     = [100, 100, 100];
    const BORDER   = [200, 200, 200];

    // ── Header: company left, PO title right ──
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.line(margin, 38, pageW - margin, 38);

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...DARK);
    doc.text(company?.company_name || currentUser.company_name || "Company", margin, 18);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
    doc.text(company?.tagline || company?.business_type || "", margin, 25);
    if (company?.gst_number) doc.text(`GST: ${company.gst_number}`, margin, 31);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...DARK);
    doc.text("PURCHASE ORDER", pageW - margin, 18, { align: "right" });
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
    doc.text(`No: ${po.po_number || po.id}`, pageW - margin, 25, { align: "right" });
    const poDate = po.created_at?.toDate
      ? po.created_at.toDate().toLocaleDateString("en-IN")
      : new Date().toLocaleDateString("en-IN");
    doc.text(`Date: ${poDate}`, pageW - margin, 31, { align: "right" });

    let y = 45;

    // ── Vendor + Order Info (plain text, no color boxes) ──
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...GRAY);
    doc.text("VENDOR", margin, y);
    doc.text("ORDER INFO", pageW / 2 + 5, y);

    y += 5;
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...DARK);
    doc.text(po.vendor_name || "—", margin, y);

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
    if (po.vendor_address) { y += 5; doc.text(po.vendor_address, margin, y, { maxWidth: 80 }); }
    if (po.vendor_phone)   { y += 5; doc.text(`Tel: ${po.vendor_phone}`, margin, y); }
    if (po.vendor_gst)     { y += 5; doc.text(`GST: ${po.vendor_gst}`, margin, y); }

    const infoRows = [
      ["PO No",       po.po_number || po.id || "—"],
      ["Date",        poDate],
      ["Status",      (po.po_status || "").toUpperCase()],
      ["Approved By", po.approved_by_name || "—"],
    ];
    let iy = 50;
    infoRows.forEach(row => {
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...DARK);
      doc.text(`${row[0]}:`, pageW / 2 + 5, iy);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...GRAY);
      doc.text(String(row[1]), pageW / 2 + 30, iy);
      iy += 6;
    });

    y = Math.max(y, iy) + 8;
    doc.setDrawColor(...BORDER);
    doc.line(margin, y - 4, pageW - margin, y - 4);

    // D-291 P3c: the vendor sees what is ordered the way it will be billed —
    // HSN, rate before GST, discount, GST %, taxable — and the order value
    // from computeOrderValue() (never received × rate).
    // Old lines saved without a line_id get a stand-in key by position.
    const activeItems = (po.items || [])
      .map((it, idx) => ({ ...it, line_id: it.line_id ?? `idx_${idx}` }))
      .filter(i => !i._cancelled);
    const fmt = (n) => `Rs. ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
    const pdfSupply = supplyTypeOf(company?.gst_number, po.vendor_gst);
    const pdfValue  = computeOrderValue(activeItems, pdfSupply);
    const tableRows = activeItems.map((item, idx) => {
      const lv = pdfValue.lines.find(l => l.line_id === item.line_id);
      const notAgreed = item.rate_not_agreed === true || !(agreedRateOf(item) > 0);
      const disc = Number(item.discount_value || 0) > 0
        ? (item.discount_type === "flat" ? fmt(item.discount_value) : `${item.discount_value}%`)
        : "";
      return [
        idx + 1,
        item.item_name + (item.is_custom_item ? " *" : ""),
        item.hsn_code || "",
        `${Number(item.ordered_qty || 0)} ${item.unit || ""}`.trim(),
        notAgreed ? "To be quoted" : fmt(agreedRateOf(item)),
        disc,
        item.gst_percent === null || item.gst_percent === undefined || item.gst_percent === "" ? "" : `${item.gst_percent}%`,
        lv?.status === "ok" ? fmt(lv.taxable) : "",
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [["#", "Item Name", "HSN", "Qty", "Rate (before GST)", "Disc.", "GST", "Taxable"]],
      body: tableRows,
      theme: "grid",
      headStyles: { fillColor: [240, 240, 240], textColor: DARK, fontSize: 8, fontStyle: "bold", halign: "center", cellPadding: 3 },
      bodyStyles: { fontSize: 8, textColor: DARK, cellPadding: 2.5 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles: {
        0: { halign: "center", cellWidth: 8 },
        1: { cellWidth: 55 },
        2: { halign: "center", cellWidth: 17 },
        3: { halign: "right", cellWidth: 20 },
        4: { halign: "right", cellWidth: 25 },
        5: { halign: "right", cellWidth: 15 },
        6: { halign: "right", cellWidth: 12 },
        7: { halign: "right", cellWidth: 28 },
      },
      margin: { left: margin, right: margin },
    });

    y = doc.lastAutoTable.finalY + 5;
    const totalRows = [["Taxable", fmt(pdfValue.taxable_total)]];
    if (pdfValue.supply_type === "intra") {
      totalRows.push(["CGST", fmt(pdfValue.cgst)], ["SGST", fmt(pdfValue.sgst)]);
    } else if (pdfValue.supply_type === "inter") {
      totalRows.push(["IGST", fmt(pdfValue.igst)]);
    } else {
      totalRows.push(["GST", fmt(pdfValue.tax_total)]);
    }
    const tX = pageW - margin - 80;
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...DARK);
    totalRows.forEach(([label, val]) => {
      doc.text(`${label}:`, tX + 3, y + 4);
      doc.text(val, tX + 77, y + 4, { align: "right" });
      y += 5;
    });
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.rect(tX, y, 80, 11);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("TOTAL ORDER VALUE:", tX + 3, y + 7.5);
    doc.text(fmt(pdfValue.total), tX + 77, y + 7.5, { align: "right" });

    let noteY = y + 16;
    doc.setFontSize(7);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...GRAY);
    if (pdfValue.rate_not_agreed.length > 0 || pdfValue.rate_missing.length > 0) {
      doc.text("Lines marked 'To be quoted' are not in the total - please quote their rate.", margin, noteY);
      noteY += 5;
    }
    if (activeItems.some(i => i.is_custom_item)) {
      doc.text("* Custom item - not in standard catalogue", margin, noteY);
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setDrawColor(...BORDER);
      doc.line(margin, 287, pageW - margin, 287);
      doc.setFontSize(6.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...GRAY);
      doc.text(
        `${company?.company_name || currentUser.company_name || "Company"} | Generated ${new Date().toLocaleString("en-IN")} | Page ${p} of ${pageCount}`,
        pageW / 2, 292, { align: "center" }
      );
    }
    doc.save(`${po.po_number || "PO"}_${(po.vendor_name || "vendor").replace(/\s+/g, "_")}.pdf`);
  }, [po, currentUser, company]);

  // ── openAmend ────────────────────────────────────────────────────────────────
  const openAmend = useCallback(() => {
    if (!po || !canAmend(po, currentUser.role)) return;
    // D-291 P3c: rows keyed by line_id (two lines with the same item no
    // longer share a key, so typing in one never changes the other).
    // _saved_index lets the service match old lines saved without a line_id.
    // base_rate = the agreed rate before GST (old lines: from `rate`).
    const items = (po.items || []).map((item, idx) => ({
      ...item,
      base_rate:       agreedRateOf(item) > 0 ? String(agreedRateOf(item)) : "",
      gst_percent:     gstBoxValue(item.gst_percent),
      hsn_code:        item.hsn_code || "",
      rate_not_agreed: item.rate_not_agreed === true,
      _rowKey:         item.line_id || `existing_${idx}`,
      _saved_index:    idx,
      _isNew:          false,
      _cancelled:      item._cancelled || false,
      _wasReceived:    (parseFloat(item.received_qty) || 0) > 0,
      // P3c-7: an Item Master line keeps its saved unit read-only; a custom
      // line, or a line saved without a unit, gets the unit list.
      _unitLocked:     !item.is_custom_item && !String(item.item_id || "").startsWith("custom_") && hasUnit(item.unit),
    }));
    setAmendNotice(null);
    setAmendItems(items);
    setAmendReason("");
    setAmendError(null);
    setSearchState({});
    loadItemsMaster();
    setShowAmend(true);
  }, [po, loadItemsMaster, currentUser.role]);

  // ── amendItemsRef ────────────────────────────────────────────────────────────
  const amendItemsRef = useRef([]);
  useEffect(() => { amendItemsRef.current = amendItems; }, [amendItems]);

  const getSuggestions = useCallback((queryStr, rowKey) => {
    // P4-3: "already in PO" by item_id - a same-name item stays pickable.
    const takenIds = amendItemsRef.current
      .filter((r) => r._rowKey !== rowKey && !r._cancelled && r.item_id)
      .map((r) => r.item_id);
    return searchPicker(itemsMasterRef.current, queryStr, { takenIds });
  }, []);

  const handleSearchChange = useCallback((rowKey, value) => {
    const suggestions = getSuggestions(value, rowKey);
    const hasQuery    = value.trim().length >= 2;
    const isCustom    = hasQuery && suggestions.length === 0;
    // P3c-8: the full name of exactly ONE Item Master item typed -> linked to
    // it, as if clicked (not when another active row already has that item).
    // P4-3: 2+ items with the name -> stays unlinked, message beside the field.
    const link   = typedNameLink(itemsMasterRef.current, value);
    const exact  = link.item || null;
    const notice = link.ambiguous ? ambiguousNameMessage(link.ambiguous) : (link.blocked || "");
    setAmendItems((prev) => {
      const taken = !!exact && prev.some((o) => o._rowKey !== rowKey && !o._cancelled && o.item_id === exact.item_id);
      return prev.map((r) => {
        if (r._rowKey !== rowKey) return r;
        if (exact && !taken) return amendRowFromMaster(r, exact);
        return { ...r, item_name: value, item_id: null, item_code: "", _isCustom: isCustom,
          // P3c-7: typing un-picks the item -> a locked unit is cleared and unlocked.
          ...(r._unitLocked || r._unitMasterMissing ? { unit: "", _unitLocked: false, _unitMasterMissing: false } : {}) };
      });
    });
    setSearchState((prev) => ({
      ...prev,
      [rowKey]: { query: value, suggestions, open: suggestions.length > 0, notice },
    }));
  }, [getSuggestions]);

  const handleSelectSuggestion = useCallback((rowKey, masterItem) => {
    if (masterItem._alreadyAdded || masterItem._blocked) return;
    setAmendItems((prev) =>
      prev.map((r) => (r._rowKey === rowKey ? amendRowFromMaster(r, masterItem) : r))
    );
    setSearchState((prev) => ({
      ...prev,
      [rowKey]: { query: masterItem.item_name, suggestions: [], open: false },
    }));
  }, []);

  const handleAddItem = useCallback(() => {
    const rowKey = `new_${Date.now()}`;
    setAmendItems((prev) => [
      ...prev,
      { _rowKey: rowKey, _isNew: true, _isCustom: false, _cancelled: false, _wasReceived: false, _unitLocked: false, item_id: null, item_name: "", item_code: "",
        item_category: "", unit: "", ordered_qty: "", received_qty: 0, base_rate: "", rate_not_agreed: false,
        discount_type: "percent", discount_value: "", gst_percent: "", hsn_code: "" },
    ]);
    setSearchState((prev) => ({ ...prev, [rowKey]: { query: "", suggestions: [], open: false } }));
  }, []);

  const handleCancelItem  = useCallback((rowKey) => {
    setAmendItems((prev) => prev.map((r) => r._rowKey === rowKey ? { ...r, _cancelled: true  } : r));
  }, []);

  const handleRestoreItem = useCallback((rowKey) => {
    setAmendItems((prev) => prev.map((r) => r._rowKey === rowKey ? { ...r, _cancelled: false } : r));
  }, []);

  const handleRowChange = useCallback((rowKey, field, value) => {
    setAmendItems((prev) => prev.map((r) => r._rowKey === rowKey ? { ...r, [field]: value } : r));
  }, []);

  // ── handleSaveAmend ───────────────────────────────────────────────────────────
  const handleSaveAmend = useCallback(async () => {
    if (!po) return;

    if (!amendReason.trim()) {
      setAmendError("Amendment reason is mandatory — please fill in the reason below.");
      setTimeout(() => {
        reasonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        reasonRef.current?.focus();
      }, 50);
      return;
    }
    const activeItems = amendItems.filter((r) => !r._cancelled);
    if (activeItems.length === 0) { setAmendError("At least one active item is required."); return; }

    try {
      setAmendSaving(true);
      setAmendError(null);

      // D-291 P3c: ONE transaction in purchaseOrderService — refused if the
      // PO changed since this page loaded it; lines merged into the saved
      // lines by line_id (D-295 — never rebuilt); every line checked by
      // checkPOLines(); landed cost recalculated there; an approved PO goes
      // back to Draft. The boxes go as typed — an empty box is never 0.
      // An Unregistered vendor's lines are sent as GST 0.
      const unregistered = vendorGstStatus(vendorDoc || {}) === "unregistered" || po.vendor_gst_registered === false;
      const lines = amendItems.map((item) => ({
        line_id:         item.line_id,
        _saved_index:    item._isNew ? undefined : item._saved_index,
        _cancelled:      item._cancelled === true,
        item_id:         item.item_id || "",
        item_name:       String(item.item_name || "").trim(),
        item_code:       item.item_code || "",
        item_category:   item.item_category || "",
        unit:            item.unit || "",
        ordered_qty:     item.ordered_qty,
        base_rate:       item.rate_not_agreed ? "" : item.base_rate,
        rate_not_agreed: item.rate_not_agreed === true,
        discount_type:   item.discount_type === "flat" ? "flat" : "percent",
        discount_value:  item.discount_value,
        gst_percent:     unregistered ? 0 : item.gst_percent,
        hsn_code:        item.hsn_code || "",
      }));

      const result = await amendPurchaseOrder({
        po_id:             poId,
        company_id:        currentUser.company_id,
        loaded_updated_at: po.updated_at,
        lines,
        reason:            amendReason.trim(),
        user:              currentUser,
      });

      setShowAmend(false);
      if (result.back_to_draft) {
        setAmendNotice("This PO was approved before the change, so it is back in Draft — it must be approved again for the new value.");
      }
      await silentReloadPO();
    } catch (e) {
      console.error("[handleSaveAmend] Error:", e);
      // The service names the line to fix, or says the PO changed — show it.
      setAmendError(e?.message || "Failed to save amendment. Please try again.");
    } finally {
      setAmendSaving(false);
    }
  }, [po, poId, amendItems, amendReason, currentUser, silentReloadPO, vendorDoc]);

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Loading purchase order...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-red-600">{error}</p>
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm">
          ← Back
        </button>
      </div>
    );
  }
  if (!po) return null;

  const statusUI       = STATUS_UI[po.po_status] || { label: po.po_status, color: "bg-gray-100 text-gray-600" };
  const grandTotal     = calcGrandTotal(po.items || []);   // RECEIVED value (received × GRN rate)

  // ── D-291 P3c: ORDERED value + vendor GST status ──────────────────────────
  const vendorStatus = vendorDoc
    ? vendorGstStatus(vendorDoc)
    : (po.vendor_gst_registered === false ? "unregistered" : po.vendor_gst_registered === true ? "registered" : "not_entered");
  const vendorUnregistered = vendorStatus === "unregistered";
  const poVendorGstin      = po.vendor_gst || vendorGstinOf(vendorDoc);
  const supplyType         = supplyTypeOf(company?.gst_number, poVendorGstin);
  // Old lines saved without a line_id get a stand-in key by position.
  const lineKeyOf          = (item, idx) => item.line_id ?? `idx_${idx}`;
  const orderValue         = computeOrderValue((po.items || []).map((it, idx) => ({ ...it, line_id: lineKeyOf(it, idx) })), supplyType);
  const lineValueOf        = (item, idx) => orderValue.lines.find(l => l.line_id === lineKeyOf(item, idx)) || null;
  const anyReceived        = (po.items || []).some(i => !i._cancelled && (parseFloat(i.received_qty) || 0) > 0);
  const gstOnUnregistered  = vendorUnregistered && (po.items || []).some(i => !i._cancelled && Number(i.gst_percent || 0) > 0);
  // Same check as Approve and Amend in the service, so the fix list below names
  // every line Approve would refuse. The service still decides.
  const lineProblems       = checkPOLines(po.items || [], { vendorUnregistered }).errors;
  // Amend modal preview — same maths, from the rows being edited.
  const amendValue = showAmend
    ? computeOrderValue(
        amendItems.map(r => ({
          line_id: r._rowKey, item_name: r.item_name, ordered_qty: r.ordered_qty,
          base_rate: r.base_rate, rate_not_agreed: r.rate_not_agreed === true,
          discount_type: r.discount_type, discount_value: r.discount_value,
          gst_percent: vendorUnregistered ? 0 : r.gst_percent, _cancelled: r._cancelled,
        })),
        supplyType
      )
    : null;
  const userRole       = currentUser.role || "";
  const amendAllowed   = canAmend(po, userRole);
  const approveAllowed = canApprove(po, userRole);
  const orderAllowed   = canMarkOrdered(po, userRole);   // TO-16
  const receiveAllowed = canReceive(po);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-700 mb-1 flex items-center gap-1">
            ← Back
          </button>
          <h1 className="text-xl font-bold text-gray-900">{po.po_number || "Purchase Order"}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Vendor: <span className="font-medium text-gray-700">{po.vendor_name || "—"}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusUI.color}`}>
            {statusUI.label}
          </span>
          {po.grn_locked && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
              GRN Locked
            </span>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          NEXT STEP (P3c-6, Ravi 21 Oct) — the first thing on the page says, in
          plain words, what this PO needs now, who has to do it, and gives the
          one button for it. Progress line: Draft → Approved → Ordered → Received.
          All PO actions (Approve, Mark as Ordered, GRN, Finalize, View GRN)
          live here — nowhere else on the page.
          ═══════════════════════════════════════════════════════════════════ */}
      {(() => {
        const isCancelled  = po.po_status === "cancelled";
        const isDone       = po.grn_locked === true || (TERMINAL_PO_STATUSES.includes(po.po_status) && !isCancelled);
        const stepIndex    = isDone ? 3 : po.po_status === "ordered" ? 2 : po.po_status === "approved" ? 1 : 0;
        const steps        = ["Draft", "Approved", "Ordered", "Received"];
        const noRateCount  = orderValue.rate_not_agreed.length + orderValue.rate_missing.length;
        const valueText    = vendorUnregistered
          ? `₹${inr(orderValue.total)} (no GST — unregistered vendor)`
          : `₹${inr(orderValue.total)} incl. GST`;
        const blockers     = [
          ...(gstOnUnregistered ? ["This vendor is unregistered (no GSTIN), but some lines carry GST — an unregistered vendor cannot charge GST. Set GST to 0."] : []),
          ...(lineProblems.length > 0 ? lineProblems : orderValue.errors),
        ];
        const isSite       = po.purchase_mode === "direct_site_purchase";
        const btn          = "px-4 py-2 text-sm font-semibold rounded-lg transition whitespace-nowrap disabled:opacity-50";

        let tone = "gray", title = "", lines = [], actions = null, errorText = null;

        if (isCancelled) {
          title = "This PO was cancelled";
        } else if (isDone) {
          // D-291 P3d-5a: goods and bill are two events - say which one is done.
          const rc   = billInfo?.receipt || null;
          const bill = billInfo?.bill || null;
          const viewGrn = po.grn_locked && (
            <button onClick={() => navigate(`/purchase-orders/${poId}/grn`)} className={`${btn} bg-white border border-gray-300 text-gray-700 hover:bg-gray-50`}>
              View GRN Receipt
            </button>
          );
          if (rc?.bill_status === "awaiting_bill" && !rc.purchase_bill_id) {
            tone  = "amber";
            title = `Material received (₹${inr(grandTotal)}) — waiting for the vendor's bill`;
            lines = [canEnterBills(currentUser)
              ? "When the vendor's bill comes, enter it here. The bill adds it to the vendor's khaata."
              : "Owner, Accounts, SM, Manager or Store enters the vendor's bill."];
            actions = (
              <>
                {viewGrn}
                {canEnterBills(currentUser) && (
                  <button onClick={() => navigate(`/purchase-bills/new/${rc.id}`)} className={`${btn} bg-indigo-600 hover:bg-indigo-700 text-white`}>
                    Enter bill
                  </button>
                )}
              </>
            );
          } else if (rc?.bill_status === "billed") {
            tone  = "green";
            title = bill
              ? `Completed — bill entered · ${bill.bill_type === "proforma" ? "Proforma (tax invoice pending)" : `Tax invoice ${bill.vendor_invoice_no || ""}`} · ₹${inr(bill.bill_total)}`
              : "Completed — bill entered";
            lines = [`Material received ₹${inr(grandTotal)}. This PO is closed and read only.`];
            actions = (
              <>
                {viewGrn}
                {bill && canEnterBills(currentUser) && (
                  <button onClick={() => navigate(`/purchase-bills/${bill.id}`)} className={`${btn} bg-indigo-600 hover:bg-indigo-700 text-white`}>
                    View bill
                  </button>
                )}
              </>
            );
          } else {
            tone  = "green";
            title = `Completed — material received (₹${inr(grandTotal)})`;
            lines = [rc?.bill_status === "not_needed"
              ? "Nothing was received, so no vendor bill is needed. This PO is closed and read only."
              : "This PO is closed and read only."];
            actions = po.grn_locked && (
              <button onClick={() => navigate(`/purchase-orders/${poId}/grn`)} className={`${btn} bg-green-600 hover:bg-green-700 text-white`}>
                ✅ View GRN Receipt
              </button>
            );
          }
        } else if (po.po_status === "draft" && blockers.length > 0) {
          tone  = "red";
          title = "Fix this PO before it can be approved";
          lines = amendAllowed ? blockers : [...blockers, "Only Owner / SM / Manager can amend this PO."];
          actions = amendAllowed && (
            <button onClick={openAmend} className={`${btn} bg-indigo-600 hover:bg-indigo-700 text-white`}>✏ Amend PO</button>
          );
        } else if (approveAllowed) {
          tone  = "amber";
          title = "Waiting for your approval";
          lines = [
            `Order value: ${valueText}`,
            ...(noRateCount > 0 ? [`${noRateCount} line(s) without an agreed rate are not in this value — the rate comes on the vendor's bill.`] : []),
          ];
          errorText = approveError;
          actions = (
            <>
              {amendAllowed && (
                <button onClick={openAmend} className={`${btn} bg-white border border-gray-300 text-gray-700 hover:bg-gray-50`}>✏ Amend PO</button>
              )}
              <button onClick={approvePO} disabled={approving} className={`${btn} bg-yellow-500 hover:bg-yellow-600 text-white`}>
                {approving ? "Approving..." : "✓ Approve PO"}
              </button>
            </>
          );
        } else if (po.po_status === "draft") {
          tone  = "gray";
          title = "Waiting for approval by SM / Owner / Manager";
          lines = [`Order value: ${valueText}`];
        } else if (po.po_status === "approved" && orderAllowed) {
          tone  = "blue";
          title = "Approved — send this PO to the vendor, then mark it Ordered";
          lines = [
            `Approved by ${po.approved_by_name || "—"}${po.approved_value?.total != null ? ` · approved value ₹${inr(po.approved_value.total)}` : ""}`,
            "Download the PDF (or use WhatsApp / Email below) and send it to the vendor.",
          ];
          errorText = markOrderedError;
          actions = (
            <>
              <button onClick={downloadPO} className={`${btn} bg-white border border-gray-300 text-gray-700 hover:bg-gray-50`}>↓ Download PDF</button>
              <button onClick={markAsOrdered} disabled={markingOrdered} className={`${btn} bg-blue-600 hover:bg-blue-700 text-white`}>
                {markingOrdered ? "Updating..." : "📦 Mark as Ordered"}
              </button>
            </>
          );
        } else if (po.po_status === "approved") {
          tone  = "blue";
          title = "Approved — waiting for SM / Owner / Manager to send it to the vendor";
          lines = [`Approved by ${po.approved_by_name || "—"}`];
        } else if (po.po_status === "ordered" && !isSite) {
          tone  = "indigo";
          title = "Ordered — waiting for the material from the vendor";
          lines = ["When the material arrives at the store, receive it here."];
          actions = receiveAllowed && hasPoRole(PO_GRN_ROLES, userRole) && (
            <button onClick={() => navigate(`/purchase-orders/receive/${poId}`)} className={`${btn} bg-green-600 hover:bg-green-700 text-white`}>
              Receive Material (GRN)
            </button>
          );
        } else if (po.po_status === "ordered" && po.site_confirmation_status === "site_confirmed") {
          tone  = "indigo";
          title = `Site confirmed by ${po.site_confirmed_by_name || "the Site Engineer"} — finalize rate & payment`;
          actions = receiveAllowed && hasPoRole(SITE_FINALIZE_ROLES, userRole) && (
            <button onClick={() => navigate(`/purchase-orders/finalize-site/${poId}`)} className={`${btn} bg-emerald-600 hover:bg-emerald-700 text-white`}>
              ✓ Finalize Rate & Payment
            </button>
          );
        } else if (po.po_status === "ordered" && po.site_confirmation_status === "finalizing") {
          tone  = "red";
          title = "Finalization started but did not finish — do not finalize again";
          lines = ["Ask the admin to check the site stock and the vendor bill first."];
        } else if (po.po_status === "ordered") {
          tone  = "indigo";
          title = `Ordered — waiting for the Site Engineer to confirm the quantity at ${po.site_name || "the site"}`;
        }

        const TONES = {
          gray:   "bg-gray-50 border-gray-200 text-gray-800",
          green:  "bg-green-50 border-green-200 text-green-900",
          red:    "bg-red-50 border-red-200 text-red-800",
          amber:  "bg-yellow-50 border-yellow-300 text-yellow-900",
          blue:   "bg-blue-50 border-blue-200 text-blue-900",
          indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
        };

        return (
          <div className="mb-6">
            {!isCancelled && (
              <div className="flex items-center mb-3 text-xs">
                {steps.map((label, i) => (
                  <React.Fragment key={label}>
                    {i > 0 && <div className={`flex-1 h-0.5 mx-2 ${i <= stepIndex ? "bg-green-500" : "bg-gray-200"}`} />}
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        i < stepIndex || (i === stepIndex && isDone) ? "bg-green-500 text-white"
                          : i === stepIndex ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-500"
                      }`}>{i < stepIndex || (i === stepIndex && isDone) ? "✓" : i + 1}</span>
                      <span className={i === stepIndex ? "font-semibold text-gray-900" : "text-gray-500"}>{label}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
            <div className={`border rounded-xl px-5 py-4 ${TONES[tone]}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide opacity-70 mb-0.5">Next step</p>
                  <p className="text-base font-bold">{title}</p>
                  {lines.length > 0 && (
                    <ul className="mt-1 text-sm space-y-0.5">
                      {lines.map((l, i) => <li key={i}>{l}</li>)}
                    </ul>
                  )}
                </div>
                {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
              </div>
              {errorText && <p className="mt-2 text-sm text-red-600 whitespace-pre-line">{errorText}</p>}
            </div>
          </div>
        );
      })()}

      {/* ── PO Meta ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-gray-500">PO Date</p>
          <p className="font-medium">
            {po.created_at?.toDate
              ? po.created_at.toDate().toLocaleDateString("en-IN")
              : po.created_at
              ? new Date(po.created_at).toLocaleDateString("en-IN")
              : "—"}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Created By</p>
          <p className="font-medium">{po.created_by_name || "—"}</p>
        </div>
        <div>
          {po.grn_locked ? (
            <>
              <p className="text-gray-500">Received Value</p>
              <p className="font-semibold text-gray-900">₹{inr(grandTotal)}</p>
              {orderValue.total > 0 && (
                <p className="text-xs text-gray-500 mt-0.5">Ordered value: ₹{inr(orderValue.total)} incl. GST</p>
              )}
            </>
          ) : (
            <>
              <p className="text-gray-500">Order Value (incl. GST)</p>
              <p className="font-semibold text-gray-900">₹{inr(orderValue.total)}</p>
              {anyReceived && (
                <p className="text-xs text-gray-500 mt-0.5">Received value: ₹{inr(grandTotal)}</p>
              )}
            </>
          )}
        </div>
        <div>
          <p className="text-gray-500">Items</p>
          <p className="font-medium">{(po.items || []).filter((i) => !i._cancelled).length}</p>
        </div>
      </div>

      {/* ── D-291 P3c: GST / line warnings (before Approve) ── */}
      {amendNotice && (
        <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          {amendNotice}
        </div>
      )}
      {/* GST / line problems are listed in the Next step card above (P3c-6). */}

      {/* ── Direct Site Purchase info (Priority 4) ── */}
      {po.purchase_mode === "direct_site_purchase" && (
        <div className="mb-4 px-4 py-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 bg-purple-200 text-purple-800 text-xs font-bold rounded-full">
              Direct Site Purchase
            </span>
            <span className="font-medium">{po.project_name} — {po.site_name}</span>
          </div>
          <p className="text-xs text-purple-700">
            {po.site_confirmation_status === "pending_site_confirmation" && "Awaiting Site Engineer to confirm received quantity."}
            {po.site_confirmation_status === "site_confirmed" && `Confirmed by ${po.site_confirmed_by_name || "Site Engineer"} — awaiting Manager to finalize rate.`}
            {po.site_confirmation_status === "completed" && "Finalized — site stock credited. The vendor's bill is entered separately (Next step above, or Awaiting Bills)."}
            {po.site_confirmation_status === "finalizing" && `Finalization started by ${po.finalizing_by_name || "a manager"} did not finish. Do not finalize again — ask admin to check site stock and the vendor bill.`}
          </p>

          {/* Reassign Site Engineer(s) — only while still awaiting
              confirmation, and only for Manager/SM/Owner. Once the
              Site Engineer has confirmed, the pin is locked (changing
              it after the fact would be meaningless). */}
          {po.site_confirmation_status === "pending_site_confirmation" &&
            hasPoRole(PO_REASSIGN_ROLES, userRole) &&
            projectSiteEngineers.length > 0 && (
            <div className="mt-3 pt-3 border-t border-purple-200">
              <p className="text-xs font-semibold text-purple-800 mb-1.5">
                Pin to specific Site Engineer(s)
              </p>
              <div className="flex flex-wrap gap-3 mb-2">
                {projectSiteEngineers.map(se => {
                  const checked = reassignSelection.includes(se.id);
                  return (
                    <label key={se.id} className="flex items-center gap-1.5 text-xs text-purple-800">
                      <input type="checkbox" checked={checked}
                        onChange={e => setReassignSelection(prev =>
                          e.target.checked ? [...prev, se.id] : prev.filter(id => id !== se.id)
                        )} />
                      {se.name || se.email || se.id}
                    </label>
                  );
                })}
              </div>
              <button
                onClick={handleReassign}
                disabled={reassigning}
                className="px-3 py-1.5 bg-purple-600 text-white text-xs font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50 transition"
              >
                {reassigning ? "Saving..." : "Update Assignment"}
              </button>
              {reassignError && <p className="text-xs text-red-600 mt-1.5">{reassignError}</p>}
              <p className="text-xs text-purple-500 mt-1">
                Leave everyone unchecked to let anyone assigned to the project confirm it.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Approval info — once ordered (while approved it is in the Next step card) ── */}
      {po.po_status === "ordered" && po.approved_by_name && (
        <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-center gap-2">
          <span>✓</span>
          <span>
            Approved by <strong>{po.approved_by_name}</strong>
            {po.approved_at?.toDate &&
              ` on ${po.approved_at.toDate().toLocaleDateString("en-IN")}`}
            {po.approved_value?.total != null && ` · approved value ₹${inr(po.approved_value.total)} (incl. GST)`}
          </span>
        </div>
      )}

      {/* ── GRN Status Badges ── */}
      {(po.po_status === "over" || po.po_status === "over_received" || po.over_received) && (
        <div className="mb-4 px-4 py-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800 flex items-center gap-2">
          <span>⚠</span>
          <span><strong>Over Received</strong> — More material received than ordered.</span>
        </div>
      )}
      {(po.po_status === "short" || po.po_status === "short_closed") && (
        <div className="mb-4 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 flex items-center gap-2">
          <span>⚠</span>
          <span><strong>Short Received</strong> — Less material received than ordered.</span>
          {po.short_close_reason && <span className="ml-1">Reason: {po.short_close_reason}</span>}
        </div>
      )}

      {/* ── Items Table ── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800 text-sm">Items</h2>
          {amendAllowed && (
            <button onClick={openAmend} className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 transition">
              ✏ Amend PO
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Item</th>
                <th className="px-4 py-3 text-right">Ordered</th>
                <th className="px-4 py-3 text-right">Rate before GST</th>
                <th className="px-4 py-3 text-right">Disc.</th>
                <th className="px-4 py-3 text-right">GST %</th>
                <th className="px-4 py-3 text-right">Taxable</th>
                <th className="px-4 py-3 text-right">Received</th>
                <th className="px-4 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(po.items || []).map((item, idx) => {
                const receivedQty = parseFloat(item.received_qty) || 0;
                const grnRate     = parseFloat(item.rate) || 0;
                const isExact     = receivedQty === (parseFloat(item.ordered_qty) || 0);
                const isOver      = receivedQty > (parseFloat(item.ordered_qty) || 0);
                const rowColor    = item._cancelled ? "bg-gray-50 opacity-60" : isOver ? "bg-orange-50" : isExact && receivedQty > 0 ? "bg-green-50" : "";
                const lv          = lineValueOf(item, idx);
                const discValue   = Number(item.discount_value || 0);

                return (
                  <tr key={item.line_id || `${item.item_id}_${idx}`} className={rowColor}>
                    <td className="px-4 py-3">
                      <p className={`font-medium ${item._cancelled ? "line-through text-gray-400" : "text-gray-900"}`}>
                        {item.item_name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {item.unit || ""}
                        {item.item_code ? ` · ${item.item_code}` : ""}
                        {item.hsn_code ? ` · HSN ${item.hsn_code}` : ""}
                      </p>
                      {item.is_custom_item && <span className="text-xs text-purple-500">Custom item</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">{item.ordered_qty ?? "—"}</td>
                    <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                      {item.rate_not_agreed === true
                        ? <span className="text-xs text-amber-600 font-medium">Rate not agreed</span>
                        : agreedRateOf(item) > 0
                        ? `₹${inr(agreedRateOf(item))}`
                        : po.grn_locked
                        ? <span className="text-xs text-gray-400">—</span>
                        : <span className="text-xs text-red-600">Rate not entered</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                      {discValue > 0 ? (item.discount_type === "flat" ? `₹${inr(discValue)}` : `${discValue}%`) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {item.gst_percent === null || item.gst_percent === undefined || item.gst_percent === ""
                        ? (item.rate_not_agreed === true
                          ? <span className="text-xs text-amber-600 font-medium">On bill</span>
                          : <span className="text-xs text-red-600">missing</span>)
                        : `${item.gst_percent}%`}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap">
                      {item._cancelled ? "—" : lv?.status === "ok" ? `₹${inr(lv.taxable)}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                      {receivedQty || "—"}
                      {receivedQty > 0 && grnRate > 0 && (
                        <p className="text-[11px] text-gray-400">at GRN ₹{inr(grnRate)}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item._cancelled ? (
                        <span className="text-xs text-gray-400">Cancelled</span>
                      ) : isOver ? (
                        <span className="text-xs text-orange-600 font-medium">Over</span>
                      ) : isExact && receivedQty > 0 ? (
                        <span className="text-xs text-green-600 font-medium">✓ Exact</span>
                      ) : po.grn_locked ? (
                        <span className="text-xs text-amber-600 font-medium">Short</span>
                      ) : (
                        <span className="text-xs text-gray-400">Pending</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* D-291 P3c: the ORDERED value (what is approved); after a GRN
                also the RECEIVED value (received qty × GRN rate) — labelled. */}
            <tfoot className="bg-gray-50 border-t border-gray-200 text-sm">
              <tr>
                <td colSpan={5} className="px-4 pt-3 pb-1 text-right text-gray-600">Taxable (ordered)</td>
                <td className="px-4 pt-3 pb-1 text-right font-medium text-gray-900">₹{inr(orderValue.taxable_total)}</td>
                <td colSpan={2} />
              </tr>
              {orderValue.supply_type === "intra" && (
                <tr>
                  <td colSpan={5} className="px-4 py-1 text-right text-gray-600">CGST + SGST</td>
                  <td className="px-4 py-1 text-right text-gray-900">₹{inr(orderValue.cgst)} + ₹{inr(orderValue.sgst)}</td>
                  <td colSpan={2} />
                </tr>
              )}
              {orderValue.supply_type === "inter" && (
                <tr>
                  <td colSpan={5} className="px-4 py-1 text-right text-gray-600">IGST</td>
                  <td className="px-4 py-1 text-right text-gray-900">₹{inr(orderValue.igst)}</td>
                  <td colSpan={2} />
                </tr>
              )}
              {!orderValue.supply_type && (
                <tr>
                  <td colSpan={5} className="px-4 py-1 text-right text-gray-600">
                    {vendorUnregistered
                      ? <>GST <span className="text-xs text-gray-400">(unregistered vendor — no GST)</span></>
                      : <>GST <span className="text-xs text-gray-400">(CGST / SGST or IGST is decided on the bill)</span></>}
                  </td>
                  <td className="px-4 py-1 text-right text-gray-900">₹{inr(orderValue.tax_total)}</td>
                  <td colSpan={2} />
                </tr>
              )}
              <tr>
                <td colSpan={5} className="px-4 pt-1 pb-3 text-right font-semibold text-gray-700">Order Value (incl. GST)</td>
                <td className="px-4 pt-1 pb-3 text-right font-bold text-gray-900">₹{inr(orderValue.total)}</td>
                <td colSpan={2} />
              </tr>
              {!po.grn_locked && (orderValue.rate_not_agreed.length > 0 || orderValue.rate_missing.length > 0) && (
                <tr>
                  <td colSpan={8} className="px-4 pb-3 text-right text-xs text-amber-600">
                    {orderValue.rate_not_agreed.length + orderValue.rate_missing.length} line(s) without an agreed rate are not in this value.
                  </td>
                </tr>
              )}
              {anyReceived && (
                <tr className="border-t border-gray-200">
                  <td colSpan={5} className="px-4 py-3 text-right font-semibold text-gray-700">Received value (received qty × GRN rate)</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">₹{inr(grandTotal)}</td>
                  <td colSpan={2} />
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Freight Reference (estimate vs actual) ── */}
      {(po.freight_amount != null || po.actual_freight_amount != null) && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-gray-800 text-sm mb-3">Freight Reference (not part of the Order Value)</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 text-xs">Estimated at PO creation</p>
              <p className="font-medium text-gray-800">
                ₹{Number(po.freight_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Actual (confirmed at GRN)</p>
              <p className="font-medium text-gray-800">
                {po.actual_freight_amount != null
                  ? `₹${Number(po.actual_freight_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                  : <span className="text-gray-400">Not yet confirmed</span>}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Amendment History ── */}
      {(po.amendment_log || []).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-gray-800 text-sm mb-3">Amendment History</h2>
          <div className="space-y-3">
            {(po.amendment_log || []).map((log, idx) => (
              <div key={idx} className="text-sm border-l-2 border-indigo-200 pl-3">
                <p className="font-medium text-gray-800">Amendment {idx + 1} — {log.amended_by_name}</p>
                <p className="text-gray-500 text-xs">
                  {log.amended_at ? new Date(log.amended_at).toLocaleString("en-IN") : "—"}
                </p>
                <p className="text-gray-700 mt-0.5">Reason: {log.reason}</p>
                <p className="text-gray-600 text-xs">
                  {log.ordered_value_after != null
                    ? `Order value after: ₹${inr(log.ordered_value_after)} (incl. GST)`
                    : `Grand total after: ₹${(log.grand_total_after || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Action Buttons Bar ── */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button
          onClick={downloadPO}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Download PO (PDF)
        </button>

        {po.po_status === "ordered" && (
          <button
            onClick={async () => {
              if (!po.vendor_id) { alert("No vendor linked to this PO."); return; }
              try {
                const vendorSnap = await getDoc(doc(db, "vendors", po.vendor_id));
                if (!vendorSnap.exists()) { alert("Vendor not found."); return; }
                const vendor = vendorSnap.data();
                if (!vendor.email) { alert("No email on file for this vendor."); return; }
                const result = await sendVendorEmail(po, vendor.email, vendor.contact_person || "");
                if (result.success) {
                  alert(`✅ Email sent to ${vendor.email}`);
                } else {
                  alert(`Email not sent: ${result.reason}\n\nCheck EmailJS configuration in PurchaseOrderDetail.js`);
                }
              } catch (e) {
                alert("Failed to send email: " + e.message);
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Resend Email to Vendor
          </button>
        )}
      </div>

      {/* Approve / Mark as Ordered / GRN / Finalize / View GRN are in the
          Next step card at the top of the page (P3c-6). */}

      {/* ═══════════════════════════════════════════════════════════════════════
          POST-APPROVE ACTION MODAL — TO-16 updated: reflects "approved" state
          ═══════════════════════════════════════════════════════════════════ */}
      {showPostApprove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  {/* TO-16: updated title — "approved" not "ordered" */}
                  <h3 className="text-base font-bold text-gray-900">PO Approved ✓</h3>
                  <p className="text-sm text-gray-500">
                    {po?.po_number} — Next step: Download PDF and mark as Ordered
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="bg-gray-50 rounded-xl px-4 py-3 mb-4">
                <p className="text-xs text-gray-500 mb-1">Vendor</p>
                <p className="text-sm font-semibold text-gray-800">{postApproveVendor?.name || "—"}</p>
                {postApproveVendor?.contact_person && (
                  <p className="text-xs text-gray-500">Attn: {postApproveVendor.contact_person}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  📧 {postApproveVendor?.email || <span className="text-red-500">No email on file</span>}
                </p>
              </div>

              {/* TO-16: next step hint */}
              <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl mb-4 text-sm text-blue-700">
                <p className="font-semibold mb-1">Next Step</p>
                <p className="text-xs">Download the PO PDF and share with your vendor (WhatsApp / Email / Print). Then click <strong>Mark as Ordered</strong> to enable GRN.</p>
              </div>

              {emailStatus === "sent" && (
                <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl mb-4 text-sm text-green-700">
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Email sent to {postApproveVendor?.email}
                </div>
              )}
              {emailStatus === "failed" && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl mb-4 text-sm text-red-700">
                  ✗ Email failed — check EmailJS configuration or send manually.
                </div>
              )}
              {emailStatus === "not_configured" && (
                <div className="px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-xl mb-4 text-sm text-yellow-700">
                  ⚠ EmailJS not configured. Download PDF and share manually.
                </div>
              )}

              <div className="space-y-2">
                <button
                  onClick={() => { downloadPO(); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PO (PDF)
                </button>

                {/* ✅ WhatsApp button */}
                {postApproveVendor?.phone ? (
                  <a
                    href={"https://wa.me/" +
                      postApproveVendor.phone
                        .replace(/[^0-9]/g, "")
                        .replace(/^0/, "91")
                        .replace(/^([6-9]\d{9})$/, "91$1") +
                      "?text=" + encodeURIComponent(
                      `Hi ${postApproveVendor.name || ""},\n\nPlease find Purchase Order *${po?.po_number}* from ${company?.company_name || "our company"}.\n\nItems:\n${
                        (po?.items || [])
                          .filter(i => !i._cancelled)
                          .map(i => `• ${i.item_name} — Qty: ${i.ordered_qty || 0} ${i.unit || ""}`)
                          .join("\n")
                      }\n\nKindly confirm receipt and delivery date.\n\nThank you.`
                      )}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white text-sm font-semibold rounded-xl hover:bg-[#20b858] transition"
                  >
                    📱 Send via WhatsApp
                  </a>
                ) : (
                  <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-400 text-center">
                    📱 WhatsApp not available — no phone number on file for this vendor
                  </div>
                )}

                <button
                  onClick={async () => {
                    if (!postApproveVendor?.email) { setEmailStatus("failed"); return; }
                    setEmailSending(true);
                    setEmailStatus(null);
                    const result = await sendVendorEmail(approvedPOData || po, postApproveVendor.email, postApproveVendor.contact_person);
                    setEmailSending(false);
                    if (result.reason === "EmailJS not configured") {
                      setEmailStatus("not_configured");
                    } else if (result.success) {
                      setEmailStatus("sent");
                    } else {
                      setEmailStatus("failed");
                    }
                  }}
                  disabled={emailSending || !postApproveVendor?.email || emailStatus === "sent"}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {emailSending ? "Sending..." : emailStatus === "sent" ? "✓ Email Sent" : "Send Email to Vendor"}
                </button>

                <button
                  onClick={() => { setShowPostApprove(false); setEmailStatus(null); setPostApproveVendor(null); setApprovedPOData(null); }}
                  className="w-full px-4 py-3 bg-gray-100 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-200 transition"
                >
                  Close — I'll mark as Ordered manually
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          AMEND PO MODAL — D-291 P3c: rate before GST + tick, discount, GST %,
          HSN per line; value preview from computeOrderValue(); saved by
          amendPurchaseOrder() (one transaction, merge by line_id).
          ═══════════════════════════════════════════════════════════════════ */}
      {showAmend && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Amend Purchase Order</h2>
                <p className="text-sm text-gray-500">{po.po_number}</p>
              </div>
              <button onClick={() => setShowAmend(false)} className="text-gray-400 hover:text-gray-600 text-xl font-light">✕</button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {masterLoading && (
                <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  Loading item catalogue...
                </div>
              )}
              {!masterLoading && itemsMaster.length > 0 && (
                <div className="mb-4 px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
                  ✓ {itemsMaster.length} items loaded
                </div>
              )}
              {!masterLoading && itemsMaster.length === 0 && (
                <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  ⚠ Item catalogue not loaded.
                </div>
              )}
              {amendError && (
                <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 whitespace-pre-line">
                  {amendError}
                </div>
              )}
              {po.po_status === "approved" && (
                <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  This PO is already approved. Saving a change sends it back to Draft — it must be approved again.
                </div>
              )}
              {vendorUnregistered && (
                <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  Unregistered vendor — every line is saved with GST 0.
                </div>
              )}

              <div className="space-y-3 mb-4">
                {amendItems.map((item) => {
                  const rowKey    = item._rowKey;
                  const rs        = searchState[rowKey] || { query: item.item_name || "", suggestions: [], open: false };
                  const qtyLocked = item._wasReceived;

                  return (
                    <div key={rowKey} className={`border rounded-lg p-4 ${item._cancelled ? "border-gray-200 bg-gray-50 opacity-70" : "border-gray-200 bg-white"}`}>
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="flex-1 min-w-[200px] relative">
                          <label className="text-xs text-gray-500 mb-1 block">Item Name</label>
                          <input
                            ref={(el) => { searchRefs.current[rowKey] = el; }}
                            type="text"
                            autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
                            value={rs.query || item.item_name || ""}
                            onChange={(e) => handleSearchChange(rowKey, e.target.value)}
                            disabled={item._cancelled || qtyLocked}
                            placeholder={masterLoading ? "Loading catalogue..." : "Type 2+ chars to search..."}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none disabled:bg-gray-100"
                          />
                          {rs.open && !item._cancelled && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-52 overflow-y-auto">
                              {rs.suggestions.map((suggestion) => {
                                const off = suggestion._alreadyAdded || !!suggestion._blocked;
                                return (
                                  <div
                                    key={suggestion.id}
                                    onClick={() => !off && handleSelectSuggestion(rowKey, suggestion)}
                                    className={`px-4 py-3 ${off ? "bg-gray-50 cursor-not-allowed" : "hover:bg-indigo-50 cursor-pointer"}`}
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <p className={`text-sm font-medium ${off ? "text-gray-400" : "text-gray-800"}`}>
                                        {itemOptionLabel(suggestion)}
                                      </p>
                                      {suggestion._alreadyAdded && (
                                        <span className="text-xs text-orange-500 font-medium whitespace-nowrap">Already in PO</span>
                                      )}
                                    </div>
                                    {suggestion._blocked ? (
                                      <p className="text-xs text-red-500 mt-0.5">{suggestion._blocked}</p>
                                    ) : (suggestion.specification || suggestion.item_category) && (
                                      <p className="text-xs text-gray-400 mt-0.5">
                                        {[suggestion.specification, suggestion.item_category].filter(Boolean).join(" · ")}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {rs.notice && !item._cancelled && <p className="text-xs text-amber-600 mt-0.5">{rs.notice}</p>}
                          {item.item_id && !String(item.item_id).startsWith("custom_") && (
                            <p className="text-xs text-green-600 mt-0.5">✓ Item Master · {item.item_code || item.item_id}</p>
                          )}
                        </div>
                        <div className="w-32">
                          <label className="text-xs text-gray-500 mb-1 block">Unit</label>
                          <UnitPicker value={item.unit} locked={item._unitLocked} masterNoUnit={item._unitMasterMissing}
                            disabled={item._cancelled} onChange={(u) => handleRowChange(rowKey, "unit", u)} />
                        </div>
                        <div className="w-28">
                          <label className="text-xs text-gray-500 mb-1 block">
                            Ordered Qty {qtyLocked && <span className="text-orange-500">(locked)</span>}
                          </label>
                          <input type="number" min={qtyStepFor(item.unit)} step={qtyStepFor(item.unit)} value={item.ordered_qty || ""} onChange={(e) => handleRowChange(rowKey, "ordered_qty", e.target.value)} disabled={item._cancelled || qtyLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none disabled:bg-gray-100" />
                        </div>
                        <div className="w-32">
                          <label className="text-xs text-gray-500 mb-1 block">Rate before GST (₹)</label>
                          <input type="number" min="0.01" step="0.01"
                            value={item.rate_not_agreed ? "" : (item.base_rate ?? "")}
                            onChange={(e) => handleRowChange(rowKey, "base_rate", e.target.value)}
                            disabled={item._cancelled || item.rate_not_agreed === true}
                            placeholder={item.rate_not_agreed ? "Not agreed" : "0"}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none disabled:bg-gray-100" />
                          <label className="flex items-center gap-1.5 mt-1 text-[11px] text-gray-500 whitespace-nowrap cursor-pointer">
                            <input type="checkbox" checked={item.rate_not_agreed === true} disabled={item._cancelled}
                              onChange={(e) => handleRowChange(rowKey, "rate_not_agreed", e.target.checked)} />
                            Rate not agreed yet
                          </label>
                        </div>
                        <div className="w-36">
                          <label className="text-xs text-gray-500 mb-1 block">Discount</label>
                          <div className="flex gap-1">
                            <select value={item.discount_type || "percent"} disabled={item._cancelled}
                              onChange={(e) => handleRowChange(rowKey, "discount_type", e.target.value)}
                              className="px-1.5 py-2 border border-gray-300 rounded-lg text-sm outline-none bg-white disabled:bg-gray-100">
                              <option value="percent">%</option>
                              <option value="flat">₹</option>
                            </select>
                            <input type="number" min="0" step="0.01" max={item.discount_type === "flat" ? undefined : 100}
                              value={item.discount_value ?? ""} disabled={item._cancelled}
                              onChange={(e) => handleRowChange(rowKey, "discount_value", e.target.value)}
                              placeholder="0"
                              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-right outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-100" />
                          </div>
                        </div>
                        <div className="w-20">
                          <label className="text-xs text-gray-500 mb-1 block">GST % *</label>
                          <input type="number" min="0" max="40" step="0.01"
                            value={vendorUnregistered ? "0" : (item.gst_percent ?? "")}
                            onChange={(e) => handleRowChange(rowKey, "gst_percent", e.target.value)}
                            disabled={item._cancelled || vendorUnregistered}
                            placeholder={item.rate_not_agreed ? "On bill" : "GST"}
                            title={item.rate_not_agreed ? "Leave blank if the vendor's bill will decide GST. Type 0 only if this item truly has no GST." : undefined}
                            className={`w-full px-2 py-2 border rounded-lg text-sm text-right outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-100 ${
                              !vendorUnregistered && !item._cancelled && !item.rate_not_agreed && String(item.gst_percent ?? "").trim() === "" ? "border-amber-400" : "border-gray-300"
                            }`} />
                        </div>
                        <div className="w-24">
                          <label className="text-xs text-gray-500 mb-1 block">HSN</label>
                          {(() => {
                            const hsnCheck = checkHsn(item.hsn_code);
                            return (
                              <>
                                <input type="text" inputMode="numeric" maxLength={8} value={item.hsn_code || ""} disabled={item._cancelled}
                                  onChange={(e) => handleRowChange(rowKey, "hsn_code", e.target.value.replace(/\s+/g, ""))}
                                  placeholder="Optional"
                                  className={`w-full px-2 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-100 ${
                                    hsnCheck.ok ? "border-gray-300" : "border-red-400"
                                  }`} />
                                {!hsnCheck.ok && <p className="text-[11px] text-red-600 mt-0.5">{hsnCheck.reason}</p>}
                              </>
                            );
                          })()}
                        </div>
                        <div className="w-28">
                          <label className="text-xs text-gray-500 mb-1 block">Taxable</label>
                          <p className="px-1 py-2 text-sm font-medium text-gray-700 whitespace-nowrap">
                            {(() => {
                              const lv = amendValue?.lines.find(l => l.line_id === rowKey);
                              if (item._cancelled) return "—";
                              if (lv?.status === "ok") return `₹${inr(lv.taxable)}`;
                              if (lv?.status === "rate_not_agreed") return <span className="text-xs text-amber-600">Rate not agreed</span>;
                              if (lv?.status === "error") return <span className="text-xs text-red-600">Check line</span>;
                              return "—";
                            })()}
                          </p>
                        </div>
                        <div className="flex items-end pb-1">
                          {item._cancelled ? (
                            <button onClick={() => handleRestoreItem(rowKey)} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium py-2">Restore</button>
                          ) : (
                            <button onClick={() => handleCancelItem(rowKey)} className="text-xs text-red-500 hover:text-red-700 font-medium py-2">Cancel</button>
                          )}
                        </div>
                      </div>
                      {item._isCustom && !item._cancelled && (
                        <p className="text-xs text-purple-500 mt-1.5 flex items-center gap-1">
                          <span>⚡</span> Custom item — not in Item Master
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <button onClick={handleAddItem} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 mb-6">
                + Add Item
              </button>

              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-6 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <p>Order Value after this change (incl. GST)</p>
                  <p className="text-xs text-gray-400">
                    Taxable ₹{inr(amendValue?.taxable_total)} + GST ₹{inr(amendValue?.tax_total)}
                    {(amendValue?.rate_not_agreed.length || 0) + (amendValue?.rate_missing.length || 0) > 0 && " · lines without a rate not included"}
                  </p>
                </div>
                <p className="font-bold text-gray-900">₹{inr(amendValue?.total)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amendment Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  ref={reasonRef}
                  value={amendReason}
                  onChange={(e) => { setAmendReason(e.target.value); if (amendError?.includes("reason")) setAmendError(null); }}
                  rows={3}
                  placeholder="Enter reason for amendment (mandatory)..."
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none resize-none ${amendError?.includes("reason") ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200">
              {!amendReason.trim() && (
                <p className="text-xs text-orange-600 mb-3 flex items-center gap-1">
                  ⚠ Amendment reason is required
                </p>
              )}
              <div className="flex items-center justify-between">
                <button onClick={() => setShowAmend(false)} disabled={amendSaving} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={handleSaveAmend} disabled={amendSaving} className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition">
                  {amendSaving ? "Saving..." : "Save Amendment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}